import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { Transition } from '@headlessui/react';
// import { Hits, Configure, SearchBox } from 'react-instantsearch-hooks-web';
// import { Category } from 'shared/types/product/Category';
// import InstantSearch from 'components/HOC/InstantSearch';
// import GoogleAnalyticsMiddleware from 'components/HOC/InstantSearch/middlewares/GoogleAnalyticsMiddleware';
// import { mapProduct } from 'helpers/algolia/map-product';
// import { useFormat } from 'helpers/hooks/useFormat';
// import useMediaQuery from 'helpers/hooks/useMediaQuery';
// import useScrollBlock from 'helpers/hooks/useScrollBlock';
// import { mediumDesktop } from 'helpers/utils/screensizes';
// import LocalizedIndex from 'providers/algolia/localized-index';
import SearchItem from './search-item';
import SearchSuggestion, { Props as SearchSuggestionProps } from './search-suggestion';
import Overlay from '../overlay';
import { MagnifyingGlassIcon as SearchIcon, XMarkIcon as CloseIcon } from '@heroicons/react/24/solid';
import CustomStats from './customstats';
import Button from '../button';
import LocalizedIndex from '@/providers/localized-index';
import InstantSearch from '@/HOC/InstantSearch';
import { Category } from '@shared/types/product/Category';
import useScrollBlock from '@/hooks/useScrollBlock';
import { mediumDesktop, mobile, desktop, bigDesktop, tablet } from '@/constants/screensizes';
import useMediaQuery from '@/hooks/useMediaQuery';
import { mapProduct } from '@/helpers/algolia/map-product';
import { Configure, Hits, SearchBox } from 'react-instantsearch-hooks-web';
import useTranslation from '@/providers/I18n/hooks/useTranslation';
import useLockScreen from '@/hooks/useLockScreen';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { localeMapping } from '@/constants/localeMapping';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
// import { PRODUCT_CODE } from 'helpers/constants/attributes';
import debounce from 'lodash.debounce';

interface Props {
  categories: Category | any;
  setShowSearchBar?: any;
  setShowSearchIcon?: any;
  focused: boolean;
  setFocused: any;
  query: string;
  setQuery: any;
  isFromDeliveryEstimator?: boolean;
  typoTolerance?: boolean;
  onValueSelect?: (x: string) => void;
}

const Search: React.FC<Props> = ({
  categories,
  setShowSearchBar,
  setShowSearchIcon,
  focused,
  setFocused,
  query,
  setQuery,
  isFromDeliveryEstimator = false,
  onValueSelect,
  typoTolerance,
}) => {
  const { locale } = useParams();
  const localeString = Array.isArray(locale) ? locale[0] : locale || '';
  const localeKey = localeString ? localeMapping[localeString] : undefined;
  const { translate } = useTranslation();
  const [isMobile, mobileWidth] = useMediaQuery(mobile);
  const { selectedBusinessUnit } = useStoreAndBusinessUnits();

  const form = useRef<HTMLFormElement>(null);

  const [numberOfHits, setNumberOfHits] = useState(0);
  const [productHits, setProductHits] = useState<any>();
  const [prevScrollY, setPrevScrollY] = useState(0);
  const locked = useLockScreen(focused);
  const [searchedViaEnter, setSearchedViaEnter] = useState(false);

  const onFocus = useCallback(() => {
    setFocused(true);
    // cleanUp();
  }, []);
  const onBlur = useCallback(() => setFocused(false), []);

  // const { formatMessage } = useFormat({ name: 'common' });

  const [isDesktop] = useMediaQuery(mediumDesktop);

  const router = useRouter();

  // Create debounced query update function
  const debouncedSetQuery = useRef(
    debounce((value: string) => {
      // Only update query if value has meaningful content or is empty (for clearing)
      if (value.length === 0 || value.trim().length >= 2) {
        setQuery(value);
      }
    }, 200) // 200ms delay
  ).current;

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSetQuery.cancel();
    };
  }, [debouncedSetQuery]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length > 50) {
      e.target.value = e.target.value.slice(0, 50);
    }
    debouncedSetQuery(e.target.value);
  }, [debouncedSetQuery]);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const target = e.target as HTMLInputElement;
      const value = target.value;
      // Cancel any pending debounced calls
      debouncedSetQuery.cancel();
      // Only update query if value has meaningful content or is empty
      if (value.length === 0 || value.trim().length >= 2) {
        setQuery(value);
        setSearchedViaEnter(true);
      }
    }
  }, [debouncedSetQuery]);

  const cleanUp = useCallback((selectedItemCode: string) => {
    setQuery('');
    setFocused(false);
    isFromDeliveryEstimator && onValueSelect?.(selectedItemCode);
    if (isDesktop) {
      setShowSearchBar(true);
    } 
  }, []);
  const onSubmit = useCallback(() => {
    if (!query || query.trim() === '') {
      setQuery('');
      return;
    }
    if (productHits?.length === 1) {
      router.push(`/${productHits[0]?.slug[localeKey || '']}/p/${productHits[0]?.sku}`);
    } else {
      !isFromDeliveryEstimator ? router.push(`/${localeString}/search?query=${encodeURIComponent(query)}`) : setQuery('');
    }
    setSearchedViaEnter(false);
    if (searchedViaEnter && (!productHits || (Array.isArray(productHits) && productHits.length === 0))) {
      router.push(`/product/see-all?searchFailed=true&searchValue=${encodeURIComponent(query)}`);
    }
    cleanUp('');
  }, [searchedViaEnter, query, router, cleanUp]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const clickedId = target.id;
      // const excludedElementId = ['searchBody', 'searchAll'];

      if (clickedId === 'searchSubmit') {
        return;
      }
      if (
        // form.current &&
        // !form.current.contains(target) &&
        !target.closest('.product-suggestions') &&
        // !excludedElementId.includes(clickedId) && !target.classList.contains('searchScrollBar')
        clickedId !== 'searchBody'
      ) {
        cleanUp('');
      }
    };

    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Tab' && focused) {
        cleanUp('');
        const activeElement = event.target as HTMLElement;
        if (!form.current?.contains(activeElement)) {
          cleanUp('');
        }
      }
    };

    if (focused) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyPress);
      const searchIcon = document.getElementById('searchIcon');
      if (searchIcon) {
        searchIcon.style.visibility = 'hidden';
      }
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyPress);
      const searchIcon = document.getElementById('searchIcon');
      if (searchIcon && query.length < 1) {
        searchIcon.style.visibility = 'visible';
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [focused, cleanUp]);

  useEffect(() => {
    const body = document.body;
    if (focused) {
      body.style.overflow = 'hidden';
      body.classList.add('modal-closed');
    } else {
      body.style.overflow = '';
      body.classList.remove('modal-closed');
    }
    return () => {
      body.style.overflow = '';
      body.classList.remove('modal-closed');
    };
  }, [focused]);

  // useEffect(() => {
  //   const handleScroll = () => {
  //     const currentScrollY = window.scrollY;
  //     if (currentScrollY > prevScrollY) {
  //       // setShowSearchBar(false);
  //       cleanUp('');
  //     } else {
  //     }
  //     setPrevScrollY(currentScrollY);
  //   };

  //   window.addEventListener('scroll', handleScroll);

  //   return () => {
  //     window.removeEventListener('scroll', handleScroll);
  //   };
  // }, [prevScrollY]);
  const COUNTRY: any = {
    au: 'AU',
    nz: 'NZ',
  };

  return (
    <InstantSearch>
      <Configure />
      <div
        className={`relative ${focused ? 'z-[300]' : 'z-[5]'} ${isFromDeliveryEstimator ? 'lg:mx-0' : '!rounded-[4px]'
          }`}
      >
        {!isFromDeliveryEstimator && (
          <div className="text-primary-black absolute right-[1rem] top-[7px] z-[12] !h-[24px] !w-[24px] bg-white stroke-2 hover:cursor-pointer lg:right-[0.9rem] xl:right-[1rem] ">
              <SearchIcon id="searchSubmit" className="h-[24px] w-[24px] text-neutral-600" onClick={onSubmit} />
          </div>
        )}
        <SearchBox
          onFocus={onFocus}
          placeholder={
            isFromDeliveryEstimator
              ? translate('common.delivery.estimator.product.code')
              : mobileWidth < mediumDesktop
                ? translate('Search for products, model number')
                : mobileWidth < bigDesktop
                  ? translate('Search for products')
                  : translate('Search for products, model number')
          }
          classNames={{
            root: `relative !rounded-[4px]`,
            form: 'relative flex items-stretch bg-white w-full quick-search  !rounded-[4px]    xl:px-0',
            input: `${!isFromDeliveryEstimator
              ? ' relative   placeholder:text-[13px] text-16 placeholder:md:text-[16px] search_bar leading-6 md:leading-8 font-medium !rounded-[4px] placeholder:text-[#333] placeholder:font-medium  bg-white  box-content border !border-gray-300 border-2 grow focus:outline-none transition h-[30px] py-[3px] px-[17px]'
              : 'w-full border border-2 !border-gray-300 rounded-[8px]'
              } searchScrollBar`,
            submit: `hidden border-l transition border-neutral-400 px-16 py-10 shrink-0 ${focused ? 'bg-white' : 'bg-white'
              }`,
            submitIcon: `!hidden w-18 h-18 stroke-0 ${focused ? 'fill-white' : 'fill-secondary-black'}`,
            reset: '!hidden absolute right-[1.5rem] top-1/2 -translate-y-1/2',
            resetIcon: 'w-[24px] h-[24px] fill-black stroke-10 !hidden',
            loadingIndicator: '',
          }}
          onKeyDown={onKeyDown}
          onInput={onChange}
          onSubmit={onSubmit}
          formRef={form}
        />
        {query && query.length > 1 && (
          <div className="product-suggestions absolute top-[43px] flex w-full flex-col bg-[#F7F7F7] pb-5  lg:flex-row  xl:mx-0 xl:w-[380px] 2xl:w-[380px]">
            <div className="flex-1 pr-4  ">
              <LocalizedIndex type="products">
                <Configure />
                <div className=" my-5 px-4 text-20 font-medium text-[#333]">Product Suggestions</div>
                {query?.trim() === '' && (
                  <span className="px-4 text-16 font-medium text-[#333] lg:text-12 3xl:text-16">
                    {'No result found'}
                  </span>
                )}
                {numberOfHits === 0 && query?.trim() !== '' && (
                  <span className="px-4 text-16 font-medium text-[#333] lg:text-12 3xl:text-16">
                    {'No product found'}
                  </span>
                )}
                {query?.trim() !== '' && (
                  <div className="">
                    <Hits
                      hitComponent={({ hit }) => (
                        <SearchItem
                          hit={{ ...hit, ...mapProduct(hit, localeString) }}
                          categories={categories}
                          onClick={cleanUp}
                          isFromDeliveryEstimator={isFromDeliveryEstimator}
                        />
                      )}
                      classNames={{
                        root: 'h-fit overflow-y-auto',
                        list: `${numberOfHits !== 0 ? 'flex' : 'hidden'
                          } flex-col gap-4 2xl:h-[45vh] xl:h-[30vh] sm:h-[25vh] h-fit sm:!max-h-[19rem] !max-h-[26rem] overflow-y-auto overscroll-none searchScrollBar`,
                      }}
                      id="searchBody"
                    />
                    <CustomStats setNumberOfHits={setNumberOfHits} setProductHits={setProductHits} />
                    {!isFromDeliveryEstimator && numberOfHits > 5 && query.length > 0 && (
                      <Button
                        className="2xl:mt-22 mt-[22px] bg-transparent cursor-pointer !p-0 !px-4 text-16 font-medium !text-[#333] underline underline-offset-2 hover:!bg-transparent hover:!text-[#666] focus:!bg-transparent active:!bg-transparent lg:mt-8 lg:text-12 3xl:text-16"
                        onClick={onSubmit}
                        id="searchAll"
                      >
                        View All
                      </Button>
                    )}
                  </div>
                )}
              </LocalizedIndex>
            </div>
          </div>
        )}
      </div>
    </InstantSearch>
  );
};

export default Search;
